import { useState } from 'react';
import { useTimeoutFn } from 'react-use';

import { Button } from '@/components/form/baseInputs/Button';
import {
  ButtonWithPopover,
  ButtonWithPopoverProps,
} from '@/components/form/baseInputs/Button/ButtonWithPopover';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import { CopyOptionsMenuItems } from '@/modules/common/CopyOptionsMenuItems/CopyOptionsMenuItems';

export interface CopyResultButtonProps {
  textToCopy: string;
}

export function CopyResultButton({ textToCopy }: CopyResultButtonProps) {
  const { showFeedback } = useFeedback();
  const [copyToClipboard] = useCopyToClipboard();
  const [hasCopied, setHasCopied] = useState(false);

  const [, , resetCopiedState] = useTimeoutFn(() => {
    setHasCopied(false);
  }, 3000);

  const handleCopy = () => {
    copyToClipboard(
      textToCopy,
      () => {
        setHasCopied(true);
        resetCopiedState();
      },
      () => {
        showFeedback('Failed to copy to clipboard');
      }
    );
  };

  return (
    <Button variant="secondary" size="xs" onClick={handleCopy}>
      {hasCopied ? 'Copied!' : 'Copy result'}
    </Button>
  );
}

export interface CopyResultPopoverProps {
  searchResponse: string;
  withCitationCopy: string;
  hideLabel?: boolean;
  buttonWithPopoverProps?: Partial<ButtonWithPopoverProps>;
}

export function CopyResultPopover({
  searchResponse,
  withCitationCopy,
  hideLabel,
  buttonWithPopoverProps,
}: CopyResultPopoverProps) {
  const { showFeedback } = useFeedback();
  const [copyToClipboard] = useCopyToClipboard();

  const handleCopyTextOnly = () => {
    copyToClipboard(
      searchResponse,
      () => {
        showFeedback('Copied text to clipboard', { variant: 'success' });
      },
      () => {
        showFeedback('Failed to copy to clipboard');
      }
    );
  };

  const handleCopyWithCitation = () => {
    copyToClipboard(
      withCitationCopy,
      () => {
        showFeedback('Copied text with summary citation to clipboard', {
          variant: 'success',
        });
      },
      () => {
        showFeedback('Failed to copy to clipboard');
      }
    );
  };

  return (
    <ButtonWithPopover
      variant="secondary"
      size="xs"
      label={hideLabel ? undefined : 'Copy result'}
      {...buttonWithPopoverProps}
    >
      <CopyOptionsMenuItems
        onCopyWithCitation={handleCopyWithCitation}
        onCopyTextOnly={handleCopyTextOnly}
        copyWithCitationSubtext="For pasting text with citation into doc summary"
        copyTextOnlySubtext="Only the answer text is copied to clipboard"
      />
    </ButtonWithPopover>
  );
}
